import { API_URL, fetchWithTimeout } from '../core/api.js';

/**
 * Shared header-content strategy for modes with a simple entry→advising flow
 * (no phase state machine, unlike AdviceMode's scoping/retrieving states):
 * fetch the Haiku descriptor on mode entry and after each message. Used by
 * TreatmentMode, which is privacy-eligible.
 *
 * Privacy is held as strategy STATE rather than re-derived from each fetch:
 * the descriptor route does return the static private title, but a failed or
 * timed-out fetch falls back to `fallbackText`, which would flash public copy
 * over a private session. Once known private, this renders statically and
 * stops fetching entirely.
 *
 * @param {string} fallbackText - Shown before the first descriptor resolves
 * @returns {Object} Header content strategy (legacy { text } shape), plus
 *   `syncPrivacy(context)` for wrappers that render ahead of `generateContent`
 */
export function createDescriptorHeaderStrategy(fallbackText) {
  const ELLIPSIS = '<span class="thinking-indicator__ellipsis"></span>';
  let isPrivate = false;

  /**
   * Fold a privacy state edge out of the incoming context and report the
   * current state. Idempotent, so a wrapper may call it before delegating.
   * @param {Object} [context]
   * @returns {boolean} Whether the session is private
   */
  const syncPrivacy = (context = {}) => {
    if (context.event === 'session:privacyModeChanged') {
      isPrivate = context.privacyMode === true;
    } else if (context.event === 'privacy:changed' && context.active === true) {
      isPrivate = true;
    }
    return isPrivate;
  };

  const fetchDescriptor = async () => {
    const sessionId = window.sessionManager?.getCurrentSessionId();
    if (!sessionId) return null;
    try {
      const response = await fetchWithTimeout(`${API_URL}/chat/descriptor/${sessionId}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (!data.success || !data.data?.descriptor) return null;
      // Surface privacyMode alongside the descriptor so callers can suppress
      // the animated ellipsis over the static "Private Conversation" text.
      return { descriptor: data.data.descriptor, privacyMode: data.data.privacyMode === true };
    } catch {
      return null;
    }
  };

  return {
    syncPrivacy,

    generateContent: async (context = {}) => {
      // Static: no fetch, no ellipsis — and nothing downstream can overwrite it.
      if (syncPrivacy(context)) return { text: 'Private Conversation' };

      const result = await fetchDescriptor();
      if (!result) return { text: `${fallbackText}${ELLIPSIS}` };
      if (result.privacyMode) {
        // Resuming into an already-private session: the route is where privacy
        // is learned, since no in-session edge fired. Latching it means a later
        // fetch failure can no longer fall back to public copy.
        isPrivate = true;
        return { text: 'Private Conversation' };
      }
      return { text: `${result.descriptor}${ELLIPSIS}` };
    },

    getUpdateTriggers: () => ['message:received', 'session:privacyModeChanged', 'privacy:changed'],

    shouldUpdate: (event, eventData) => {
      if (event === 'privacy:changed') {
        if (eventData?.active === true) return true;
        // Off edge: clear WITHOUT re-rendering. Clearing is load-bearing —
        // `switchMode` early-returns on a same-mode call, so this closure can
        // outlive the private session and would otherwise pin the private
        // title onto the next PUBLIC one. Not re-rendering is deliberate: this
        // edge fires on dashboard navigation, where the header is in its
        // dashboard state and must not be written to.
        isPrivate = false;
        return false;
      }
      return true;
    }
  };
}

/**
 * Abstract base class for chat operational modes
 * Each mode defines how messages are routed, processed, and responded to
 */
export class BaseMode {
  /**
   * @param {Object} config - Mode configuration
   * @param {string} config.name - Mode name (e.g., 'ADVICE', 'DISCOVERY')
   * @param {Object} config.contextBuilder - Context builder instance
   */
  constructor(config) {
    this.name = config.name;
    this.contextBuilder = config.contextBuilder;
    this.state = {}; // Mode-specific state
  }

  /**
   * Lifecycle: Called when entering this mode
   * @param {BaseMode|null} previousMode - Mode being exited
   * @param {Object} transitionData - Context data for transition
   */
  async onEnter(previousMode, transitionData = {}) {
    console.log(`[${this.name}Mode] Entering from ${previousMode?.name || 'none'}`, transitionData);
  }

  /**
   * Lifecycle: Called when exiting this mode
   * @param {BaseMode} nextMode - Mode being entered
   * @param {Object} transitionData - Context data for transition
   */
  async onExit(nextMode, transitionData = {}) {
    console.log(`[${this.name}Mode] Exiting to ${nextMode.name}`);
  }

  /**
   * Pre-process user message before routing
   * @param {string} message - User message
   * @param {Object} context - Current context
   * @returns {Object} Processed message with additional context
   */
  preprocessMessage(message, context) {
    return { message, context };
  }

  /**
   * Determine routing strategy for this message
   * @param {string} message - User message
   * @param {Object} context - Current context
   * @returns {('llm'|'programmatic'|'hybrid')} Routing strategy
   */
  getRoutingStrategy(message, context) {
    // Default: route all messages to LLM
    // Override in subclasses for custom logic
    return 'llm';
  }

  /**
   * Generate programmatic response (no LLM call)
   * @param {string} message - User message
   * @param {Object} context - Current context
   * @returns {Promise<Object>} Response object with structure:
   *   - type: 'instant' for immediate display
   *   - text: Message text to display
   *   - messageType: Optional. Controls UI behavior (e.g., action bar suppression)
   *     - 'system': System/welcome messages (suppresses action bar)
   *     - 'error': Error messages (suppresses action bar)
   *     - 'notification': Notification messages (suppresses action bar)
   *     - undefined: Normal assistant message (shows action bar)
   *   - components: Optional array of interactive components
   */
  async generateProgrammaticResponse(message, context) {
    throw new Error(`${this.name}Mode.generateProgrammaticResponse() not implemented`);
  }

  /**
   * Post-process LLM response before rendering
   * Can extract action markers, transform text, etc.
   * @param {string} response - LLM response text
   * @param {Object} context - Current context
   * @returns {Object} { text, actions, metadata }
   */
  postprocessResponse(response, context) {
    return { text: response, actions: [], metadata: {} };
  }

  /**
   * Build mode-specific context for LLM
   * @param {string} userId - User ID
   * @param {string} message - User message
   * @param {Array} messageHistory - Recent message history
   * @returns {Promise<Object>} Context object for LLM
   */
  async buildContext(userId, message, messageHistory) {
    // Default implementation - can be overridden
    return {
      mode: this.name,
      recentMessages: messageHistory.slice(-10) // Last 10 messages
    };
  }

  /**
   * Filter message history to get relevant messages for this mode
   * @param {Array} allMessages - All messages in session
   * @returns {Array} Filtered messages
   */
  filterHistory(allMessages) {
    // Default: return all messages
    // Override to filter by mode, type, etc.
    return allMessages;
  }

  // ===== HEADER SYSTEM =====

  /**
   * Header System
   *
   * The header is universal across all modes. A mode influences it in two ways:
   *   - getModeLabel() — the plain left-hand label (one shot, on mode change)
   *   - getHeaderContentStrategy() — the middle row, and optionally a rich
   *     left-hand mode block in place of the plain label
   */

  /**
   * Get header content strategy for this mode
   *
   * Strategy Interface:
   * {
   *   generateContent(context): Promise<
   *     { text: string }                                   // legacy: middle row only
   *     | { leftBlock: null | { title, subtitle?, percent?, interactive? },
   *         middleBlock: { html: string } }
   *   >,
   *   getUpdateTriggers(): string[],
   *   shouldUpdate(event, context): boolean
   * }
   *
   * A null (or omitted) leftBlock leaves the plain #header-mode-label span in
   * place, whose text comes from getModeLabel(). Only modes that need progress
   * or a two-line identity (DISCOVERY, Foundation INTAKE) emit a leftBlock.
   *
   * @returns {Object|null} Strategy object or null if no dynamic content
   *
   * Example implementation:
   *
   * getHeaderContentStrategy() {
   *   return {
   *     async generateContent(context) {
   *       // Generate text based on mode state
   *       return { text: 'Working on Module X' };
   *     },
   *
   *     getUpdateTriggers() {
   *       // Return event names that should trigger content updates
   *       return ['module:changed', 'questionnaire:loaded'];
   *     },
   *
   *     shouldUpdate(event, context) {
   *       // Return true if content should be regenerated for this event
   *       return true;
   *     }
   *   };
   * }
   */
  getHeaderContentStrategy() {
    // Default: no dynamic content strategy
    // Override in subclasses to provide mode-specific dynamic header content
    return null;
  }

  /**
   * Static, non-interactive label describing the current task for this mode
   * (e.g. "Experiment Report", "Discussing Guide"). Rendered once per
   * mode/variant in the header's left slot — distinct from the dynamic
   * center text produced by getHeaderContentStrategy().
   *
   * Ignored by modes whose strategy emits a leftBlock, which replaces this
   * label with the richer mode block.
   * @returns {string} Mode label, or '' for no label
   */
  getModeLabel() {
    return '';
  }
}
